import GuestProps from '@interfaces/guest/GuestProps';
import { guestAxiosEnv } from '@helpers/Axios';
import SectionInfoVO from '@interfaces/sections/SectionInfoVO';
import ProfileContentVO, { ProfileContentType } from '@interfaces/contents/ProfileContentVO';
import { ProfileEntityType } from '@components/search/ProfileSearchProps';
import ProfileVO, { ProfileBuilder, ProfileType } from '@interfaces/profiles/ProfileVO';
import ProfileRequestAccessVO from '@interfaces/profiles/ProfileRequestAccessVO';
import { AccessibilityVO, ProfileAccessType } from '@interfaces/profiles/AccessibilityVO';
import SharedViewDetails from '@interfaces/guest/SharedViewDetails';
import { getUrlParam } from '../URLParamUtil';

export enum ProfilePage {
  PREVIEW_PROJECT = 'PREVIEW_PROJECT',
  PREVIEW_DOCUMENT = 'PREVIEW_DOCUMENT',
  PREVIEW_PQ_ATTACHMENT = 'PREVIEW_PQ_ATTACHMENT',
  PROFILE_VIEW = 'PROFILE_VIEW',
  PUBLIC_PROFILE_VIEW = 'PUBLIC_PROFILE_VIEW',
  PUBLIC_PREVIEW_PROJECT = 'PUBLIC_PREVIEW_PROJECT',
  PUBLIC_PREVIEW_DOCUMENT = 'PUBLIC_PREVIEW_DOCUMENT'
}

export const getRedirectTo = (): string => {
  let redirectTo: string | null = getUrlParam('redirectTo');
  if (redirectTo && redirectTo.includes(',')) {
    const history: string[] = redirectTo.split(',');
    const historyLen: number = history.length;
    redirectTo = history[historyLen - 1];
  }
  return redirectTo || '';
};

export const getBackNavigationUrl = (guestProps: GuestProps) => {
  let backUrl: string = '';
  const searchUrl: URLSearchParams = new URLSearchParams(window.location.search);
  const currPodName = searchUrl.get('podName') || guestAxiosEnv.podName;
  const podNameParam = currPodName ? `podName=${currPodName}` : '';
  const previousPage: string = getRedirectTo() || (guestProps.isPublicUser ? ProfilePage.PUBLIC_PROFILE_VIEW : ProfilePage.PROFILE_VIEW);
  switch (previousPage) {
    case ProfilePage.PROFILE_VIEW:
      backUrl = `/profile-view?${podNameParam}${searchUrl.get('id') ? `${podNameParam ? '&' : ''}cId=${searchUrl.get('id')}` : ''}${searchUrl.get('sectionId') ? `&sId=${searchUrl.get('sectionId')}` : ''}`;
      break;
    case ProfilePage.PUBLIC_PROFILE_VIEW: {
      const baseUrl = guestProps.isCustomDomain ? '/' : `/profile-center/${guestProps.profileId}/${guestProps.companyId}`;
      const queryParams = [];
      if (!guestProps.isCustomDomain && currPodName) {
        queryParams.push(podNameParam);
      }
      if (searchUrl.get('id')) {
        queryParams.push(`cId=${searchUrl.get('id')}`);
      }
      if (searchUrl.get('sectionId')) {
        queryParams.push(`sId=${searchUrl.get('sectionId')}`);
      }
      backUrl = `${baseUrl}${queryParams.length ? `?${queryParams.join('&')}` : ''}`;
      break;
    }
    case ProfilePage.PREVIEW_PROJECT:
      backUrl = `${guestProps.guestKey ? '/profile-view/' : ''}preview-content?sectionId=${searchUrl.get('sectionId')}&id=${searchUrl.get('id')}&type=PROJECT&currentSection=${searchUrl.get('currentSection')}&qId=${searchUrl.get('prevParent')}&fId=${searchUrl.get('prevFile')}`;
      break;
    case ProfilePage.PUBLIC_PREVIEW_PROJECT: {
      const pqBaseUrl = guestProps.isCustomDomain ? '/' : `/profile-center/${guestProps.profileId}/${guestProps.companyId}/`;
      const pqQueryParams = [];
      if (!guestProps.isCustomDomain && currPodName) {
        pqQueryParams.push(podNameParam);
      }
      if (searchUrl.get('sectionId')) {
        pqQueryParams.push(`sectionId=${searchUrl.get('sectionId')}`);
      }
      if (searchUrl.get('id')) {
        pqQueryParams.push(`id=${searchUrl.get('id')}`);
      }
      pqQueryParams.push('type=PROJECT');
      if (searchUrl.get('currentSection')) {
        pqQueryParams.push(`currentSection=${searchUrl.get('currentSection')}`);
      }
      if (searchUrl.get('prevParent')) {
        pqQueryParams.push(`qId=${searchUrl.get('prevParent')}`);
      }
      if (searchUrl.get('prevFile')) {
        pqQueryParams.push(`fId=${searchUrl.get('prevFile')}`);
      }
      backUrl = `${pqBaseUrl}preview-content${pqQueryParams.length ? `?${pqQueryParams.join('&')}` : ''}`;
      break;
    }
    default:
    // do nothing
  }

  let history: string | null = searchUrl.get('redirectTo');
  if (history?.includes(',')) {
    const prevTabs = history.split(',');
    history = prevTabs.slice(0, prevTabs.length - 1).join(',');
  } else {
    history = '';
  }
  if (history) {
    backUrl += (!backUrl.includes('?') ? '?redirectTo=' : '&redirectTo=') + history.replaceAll(',', '%2C');
  }
  return backUrl;
};

export const constructRedirectToParam = (prevPage: ProfilePage, ignoreEncode?: boolean, customRedirectTo?: string): string => {
  let redirectTo: string | null = customRedirectTo || getUrlParam('redirectTo');
  const seperator: string = ignoreEncode ? ',' : '%2C';
  if (redirectTo === null) {
    redirectTo = prevPage;
  } else if (!redirectTo.includes(',')) {
    redirectTo += seperator + prevPage;
  } else {
    redirectTo = redirectTo.replaceAll(',', seperator) + (!redirectTo.includes(prevPage) ? seperator + prevPage : '');
  }
  return redirectTo;
};

export const getSectionVOMap = (sectionsList: SectionInfoVO[]) => {
  const sectionVOMap: { [sId: string]: SectionInfoVO } = {};
  const contentVOMap: { [sId: string]: ProfileContentVO } = {};
  const contentIdBySectionIdMap: { [sId: string]: string[] } = {};
  const contentIds: string[] = [];

  if (!sectionsList || sectionsList.length === 0) {
    return { sectionVOMap, contentVOMap, contentIdBySectionIdMap, contentIds };
  }
  sectionsList.forEach((sectionVO, index) => {
    sectionVO.isExpanded = index === 0;
    sectionVOMap[sectionVO.id] = sectionVO;
    contentIdBySectionIdMap[sectionVO.id] = [];
    if (!sectionVO.contents || sectionVO.contents.length === 0) {
      return;
    }

    let projectCount = 0;
    let documentCount = 0;

    sectionVO.contents.forEach(contentVO => {
      if (contentVO.contentType === ProfileContentType.PROJECT) {
        projectCount += 1;
      } else if (contentVO.contentType === ProfileContentType.DOCUMENT) {
        documentCount += 1;
      } else {
        // do nothing
      }

      contentIds.push(contentVO.id);
      contentVOMap[contentVO.id] = contentVO;
      contentIdBySectionIdMap[sectionVO.id].push(contentVO.id);
    });
    sectionVOMap[sectionVO.id].projectCount = projectCount;
    sectionVOMap[sectionVO.id].documentCount = documentCount;
  });
  return { sectionVOMap, contentVOMap, contentIdBySectionIdMap, contentIds };
};

export const updateAccessVerifiedStatus = (isAccessVerified: boolean) => {
  sessionStorage.setItem('guestAccessVerified', JSON.stringify(isAccessVerified));
};

export const getContentKeyForDownload = (contentType: string) => {
  let key = contentType;
  switch (contentType) {
    case ProfileEntityType.PD:
    case ProfileContentType.DOCUMENT:
      key = 'docIds';
      break;
    case ProfileEntityType.PQ:
    case ProfileContentType.PROJECT:
      key = 'questionnaireIds';
      break;
    case ProfileEntityType.PQ_ATTACHMENT:
      key = 'attachmentIds';
      break;
    default:
      break;
  }

  return key;
};

export const skipToastMessagesMsgList = ['INVALID_ACCESS_TO_POST_MESSAGE', 'ACCESS_DENIED'];

export const isDiffOrigin = (url: string): boolean => {
  try {
    const currentOrigin = window.location.origin;
    const targetUrl = new URL(url, window.location.href);
    return targetUrl.origin !== currentOrigin;
  } catch {
    return false;
  }
};

export const EXTERNAL_GUEST = 'EXTERNAL_GUEST';

export const WATERMARK_PREFERENCES: string[] = ['enable_watermark', 'enable_watermark_view'];

export const isCustomPublicProfile = (builderType: ProfileBuilder, profileType: ProfileType): boolean => {
  return builderType === ProfileBuilder.CUSTOM && profileType === ProfileType.PUBLIC;
};

/** Custom builder: show Get Access / hidden-artifacts messaging only when request access is on (profile or content), not NDA-only. */
export const customBuilderHasRequestAccessGating = (profileVO: ProfileVO, contentVOMap: { [id: string]: ProfileContentVO }): boolean => {
  if (profileVO?.requestAccess) {
    return true;
  }
  return Object.values(contentVOMap || {}).some(item => item.requestAccess);
};

/**
 * Landing-page TCs only: selector is hidden until verified, so show Verify Access when the TC
 * has access-controlled (locked) content anywhere. Independent of whether the landing view itself
 * is request-access gated (that only affects Get Access labeling).
 */
export const shouldShowSharedViewVerifyAccess = (
  builderType: ProfileVO['builderType'] | undefined,
  profileType: ProfileVO['type'] | undefined,
  isAccessVerified: boolean,
  sharedViewList: SharedViewDetails[] | null | undefined,
  hasAccessControlledContent: boolean,
  landingPageViewId?: string | null
): boolean => {
  return (
    Boolean(landingPageViewId) && builderType === ProfileBuilder.CUSTOM && profileType === ProfileType.PUBLIC && !isAccessVerified && Boolean(sharedViewList?.length) && hasAccessControlledContent
  );
};

/**
 * Landing-page body banner:
 * - Get Access when request-access gating applies on this page
 * - Verify Access when TC has locked content (typically other Shared Views)
 */
export const shouldShowLandingPageAccessBanner = (
  builderType: ProfileVO['builderType'] | undefined,
  profileType: ProfileVO['type'] | undefined,
  isAccessVerified: boolean,
  isPublicUser: boolean,
  landingPageViewId: string | null | undefined,
  sharedViewList: SharedViewDetails[] | null | undefined,
  hasAccessControlledContent: boolean,
  hasRequestAccessGating: boolean,
  hasGatedContentOrProfile: boolean
): boolean => {
  if (!landingPageViewId || !isPublicUser || isAccessVerified || builderType !== ProfileBuilder.CUSTOM || profileType !== ProfileType.PUBLIC) {
    return false;
  }
  if (hasRequestAccessGating && hasGatedContentOrProfile) {
    return true;
  }
  return shouldShowSharedViewVerifyAccess(builderType, profileType, isAccessVerified, sharedViewList, hasAccessControlledContent, landingPageViewId);
};

export const hasSharedViewIds = (requestAccess: ProfileRequestAccessVO): boolean => {
  return !!requestAccess?.preferences?.accessibility_details?.sharedViewIds?.length;
};

/** True while opening a Quick Link copied for the Landing Page view (`linkToken` with a matching `viewId`). */
export const isLandingPageQuickLink = (landingPageViewId?: string | null): boolean => Boolean(landingPageViewId) && getUrlParam('viewId') === landingPageViewId && Boolean(getUrlParam('linkToken'));

/** Landing Page id for Quick Link copy, set only while the visitor is on that view. */
export const getActiveLandingPageViewId = (landingPageViewId?: string | null, sharedViewId?: string | null): string =>
  landingPageViewId && sharedViewId === landingPageViewId ? landingPageViewId : '';

export const getAccessibilityVO = (
  basicProfileVO: ProfileVO | undefined,
  sharedViewList: SharedViewDetails[] | undefined,
  accessibilityVO: AccessibilityVO | undefined,
  contentIdBySectionId: { [sId: string]: string[] },
  contentIds: string[]
): AccessibilityVO => {
  const builderType = basicProfileVO?.builderType;
  const profileType = basicProfileVO?.type;
  const isCustomPublic = builderType === ProfileBuilder.CUSTOM && profileType === ProfileType.PUBLIC;

  // If condition is met (sharedViewList exists AND custom public profile), construct new accessibilityVO
  if (sharedViewList && sharedViewList?.length > 0 && isCustomPublic) {
    return {
      accessType: ProfileAccessType.PARTIAL,
      includeSummary: accessibilityVO?.includeSummary || false,
      sections: Object.keys(contentIdBySectionId) || [],
      contents: contentIds || []
    };
  }

  // Otherwise, return existing accessibilityVO from store
  return accessibilityVO || { accessType: ProfileAccessType.FULL };
};

export const ARTIFACT_UPDATE_LABEL_KEY = 'ARTIFACT_HAS_BEEN_UPDATED';
